import React, { useState } from 'react';
import { BUTTON_GHOST, BUTTON_QUIET } from './adminStyles';
import { deleteCallSheet } from '../../utils/callSheets';

/**
 * Deleting a call sheet (13 Sep 2026).
 *
 * Sits at the foot of the builder rather than on the list, deliberately: you
 * should have the thing in front of you before you destroy it, and a delete
 * control on a list row is one mis-tap away from taking the wrong day.
 *
 * It also says what is about to go, counted from what's actually on the sheet
 * — "3 schedule items, 4 shots, 3 kit items" beats "are you sure?", which
 * asks a question the person can't answer without leaving the dialog.
 *
 * The database cascades: schedule, shots, kit and contacts go with it, and
 * the crew and client links stop working immediately. There is no undo, so
 * the copy says so rather than implying a wastebasket somewhere.
 */
export function CallSheetDelete({
  callSheetId,
  name,
  counts,
  onDeleted,
  onError
}: {
  callSheetId: string;
  name: string;
  counts: {
    schedule: number;
    shots: number;
    kit: number;
    contacts: number;
  };
  onDeleted: () => void;
  onError: (message: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const losing = [counts.schedule && `${counts.schedule} schedule item${counts.schedule === 1 ? '' : 's'}`, counts.shots && `${counts.shots} shot${counts.shots === 1 ? '' : 's'}`, counts.kit && `${counts.kit} kit item${counts.kit === 1 ? '' : 's'}`, counts.contacts && `${counts.contacts} contact${counts.contacts === 1 ? '' : 's'}`].filter((part): part is string => Boolean(part));
  async function remove() {
    setDeleting(true);
    const result = await deleteCallSheet(callSheetId);
    setDeleting(false);
    if (!result.ok) {
      onError(result.error ?? 'The call sheet could not be deleted.');
      return;
    }
    onDeleted();
  }
  if (!confirming) {
    return <button type="button" onClick={() => setConfirming(true)} className={BUTTON_QUIET} data-id="element-637" data-mp="3o1ruk4cq1vjw">
        Delete Call Sheet
      </button>;
  }
  return <div className="w-full max-w-xl border border-paper/25 px-6 py-6" data-id="element-638" data-mp="3ifr2gedcykhf">
      <p className="font-display text-xl leading-snug" data-id="element-639" data-mp="3icwj6q62z5pl">Delete “{name}”?</p>
      <p className="label-sm mt-3 leading-relaxed text-paper/40" data-id="element-640" data-mp="34h18v8pqz2xt">
        {losing.length > 0 ? `This also deletes ${losing.join(', ')}. The crew and client links stop working straight away, and none of it can be undone.` : 'The crew and client links stop working straight away. This cannot be undone.'}
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-5" data-id="element-641" data-mp="3nec50kg31g91">
        <button type="button" onClick={() => void remove()} disabled={deleting} className="label border border-paper bg-paper px-6 py-3.5 text-ink transition-colors duration-150 ease-out hover:bg-paperDeep disabled:opacity-50" data-id="element-642" data-mp="3ndsu3nouns4i">
          {deleting ? 'Deleting…' : 'Yes, delete it'}
        </button>
        <button type="button" onClick={() => setConfirming(false)} className={BUTTON_GHOST} data-id="element-643" data-mp="29ehehmi4mss7">
          Keep it
        </button>
      </div>
    </div>;
}